/**
 * `routing` — the onward action the visitor sees after a diagnosis
 * ("where leads go", onboarding screen 8). Shown as "After diagnosis" so it
 * is not confused with Routing rules (which contractor gets a lead).
 *
 * `routing.urgency_labels` is stored but nothing reads it (the widget shows
 * no urgency label), so it is not shown; a stored value is kept.
 *
 * The contractor roster (`/partner/vendors`) lives on its own screen
 * (screens/Contractors.tsx). Routing rules, the accept deadline and its
 * cascade (`/partner/routing`) are still contract-only.
 */
import { Choices } from '../../ui/kit';
import { ROUTING_MODES, type RoutingMode } from '../model';
import { AppHandoffStatus, PathSelect, PathSwitch, SectionCard, useAppHandoff, usePath } from './common';
import { RoutingActionsSection } from './RoutingActionsSection';

export const ROUTING_MODE_COPY: Record<RoutingMode, { title: string; description: string }> = {
  partner_dispatch: {
    title: 'Send the request to my team',
    description: 'The visitor leaves their details; you get the lead with photo and diagnosis by email or webhook.',
  },
  partner_call: {
    title: 'Let them call us',
    description: 'A call button with your number (or a matched contractor’s). No details leave the browser until they dial.',
  },
  partner_booking: {
    title: 'Let them book a visit',
    description: 'Same contact form, filed as a booking request.',
  },
  // Not "Continue in the FinderlyFix app": that is the App handoff toggle's
  // name, and one name for two settings confused the third live review.
  finderly_app: {
    title: 'Send visitors to the FinderlyFix app instead',
    description: 'Default: the full DIY guide in the FinderlyFix app. You receive no lead.',
  },
  diy_only: {
    title: 'Diagnosis and DIY tips only',
    description: 'No button to your team: the diagnosis and DIY tips are the whole experience.',
  },
};

export function RoutingModePicker() {
  const mode = usePath<RoutingMode>('routing.mode');
  const app = useAppHandoff();
  // Both app-only modes lean on the app handoff: its state, read-only here.
  const appMatters = mode.value === 'finderly_app' || mode.value === 'diy_only';
  const context =
    mode.value !== 'finderly_app' || app.where === 'every'
      ? undefined
      : app.where === 'off'
        ? 'So visitors get no onward button at all.'
        : 'So visitors with a problem you cover get no onward button.';
  return (
    <div className="stack-sm">
      <Choices
        legend="What the visitor can do after the diagnosis"
        value={mode.value}
        onChange={mode.set}
        options={ROUTING_MODES.map((m) => ({ value: m, ...ROUTING_MODE_COPY[m] }))}
      />
      {appMatters ? <AppHandoffStatus context={context} /> : null}
    </div>
  );
}

export function RoutingSection() {
  const mode = usePath<RoutingMode>('routing.mode');
  return (
    <SectionCard id="sec-routing" title="After diagnosis" description="What the visitor can do once they have a diagnosis.">
      <RoutingModePicker />
      <hr className="divider" />
      <PathSwitch path="routing.diy_first" label="Show DIY steps before the onward button" description="Visitors who can fix it themselves see how first." />
      <PathSelect
        path="routing.primary_cta"
        label="Which button is the prominent one"
        options={[
          { value: 'route', label: 'The onward action' },
          { value: 'diy', label: 'The DIY guide' },
        ]}
      />
      {mode.value === 'partner_call' ? (
        <PathSelect
          path="routing.call.phone_lookup"
          label="Which number the call button dials"
          hint="Roster lookup dials the matched contractor from your roster (Setup → Contractors)."
          options={[
            { value: 'roster', label: 'The matched contractor from my roster' },
            { value: 'static', label: 'A fixed number' },
          ]}
        />
      ) : null}
      <hr className="divider" />
      <RoutingActionsSection />
    </SectionCard>
  );
}
